import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

interface Banner {
  id: number;
  posicion: string;
  titulo: string;
  url: string;
  url_destino: string;
  activo: boolean;
  orden: number;
  creado_en: string;
}

export default function Banners() {
  const [banners, setBanners]       = useState<Banner[]>([]);
  const [cargando, setCargando]     = useState(true);
  const [subiendo, setSubiendo]     = useState(false);
  const [posicion, setPosicion]     = useState<'superior' | 'inferior' | 'marcas'>('superior');
  const [titulo, setTitulo]         = useState('');
  const [urlDestino, setUrlDestino] = useState('');
  const [preview, setPreview]       = useState<string | null>(null);
  const [archivo, setArchivo]       = useState<File | null>(null);
  const [error, setError]           = useState('');
  const [exito, setExito]           = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const dragItem = useRef<number | null>(null);
  const dragOver = useRef<number | null>(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const cargar = () => {
    setCargando(true);
    axios.get(`${API}/banners/`, { headers })
      .then(r => setBanners(r.data))
      .catch(() => {})
      .finally(() => setCargando(false));
  };

  useEffect(() => { cargar(); }, []);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setArchivo(f);
    setPreview(URL.createObjectURL(f));
  };

  const subir = async () => {
    if (!archivo) { setError('Seleccione una imagen'); return; }
    setSubiendo(true); setError(''); setExito('');
    try {
      const fd = new FormData();
      fd.append('file', archivo);
      fd.append('posicion', posicion);
      fd.append('titulo', titulo);
      fd.append('url_destino', urlDestino);
      await axios.post(`${API}/banners/`, fd, { headers });
      setExito('Banner subido correctamente');
      setArchivo(null); setPreview(null);
      setTitulo(''); setUrlDestino('');
      if (fileRef.current) fileRef.current.value = '';
      cargar();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Error al subir el banner');
    } finally {
      setSubiendo(false);
    }
  };

  const eliminar = async (id: number) => {
    if (!window.confirm('¿Eliminar este banner?')) return;
    try {
      await axios.delete(`${API}/banners/${id}`, { headers });
      cargar();
    } catch { setError('Error al eliminar'); }
  };

  const toggleActivo = async (id: number) => {
    try {
      await axios.put(`${API}/banners/${id}/toggle`, {}, { headers });
      cargar();
    } catch { setError('Error al cambiar estado'); }
  };

  const onDragStart = (index: number) => { dragItem.current = index; };
  const onDragEnter = (index: number) => { dragOver.current = index; };

  const onDragEnd = async (posicionGrupo: string) => {
    if (dragItem.current === null || dragOver.current === null) return;
    if (dragItem.current === dragOver.current) return;

    const grupo = banners.filter(b => b.posicion === posicionGrupo);
    const nuevaLista = [...grupo];
    const item = nuevaLista.splice(dragItem.current, 1)[0];
    nuevaLista.splice(dragOver.current, 0, item);
    dragItem.current = null;
    dragOver.current = null;

    const reordenados = nuevaLista.map((b, i) => ({ ...b, orden: i }));
    const otroGrupo = banners.filter(b => b.posicion !== posicionGrupo);
    setBanners([...otroGrupo, ...reordenados]);

    try {
      await axios.put(`${API}/banners/reordenar`,
        reordenados.map((b, i) => ({ id: b.id, orden: i })),
        { headers });
    } catch { setError('Error al guardar el orden'); }
  };

  const superiores = banners.filter(b => b.posicion === 'superior').sort((a,b) => a.orden - b.orden);
  const inferiores = banners.filter(b => b.posicion === 'inferior').sort((a,b) => a.orden - b.orden);
  const logosMarcas = banners.filter(b => b.posicion === 'marcas').sort((a,b) => a.orden - b.orden);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <style>{`
        [draggable]:active { opacity: 0.5; transform: scale(0.97); border: 2px solid #1565C0 !important; box-shadow: 0 20px 40px rgba(21,101,192,0.3) !important; }
        [draggable] { transition: opacity 0.2s, transform 0.2s, box-shadow 0.2s; }
      `}</style>
      <h1 className="text-2xl font-black text-azul mb-1">Banners Publicitarios</h1>
      <p className="text-gray-500 text-sm mb-8">Gestione los banners. <strong>Arrastre</strong> las imagenes para reordenarlas.</p>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8">
        <h2 className="font-black text-azul text-lg mb-5">Subir nuevo banner</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Posicion</label>
              <div className="flex gap-3">
                {(['superior', 'inferior', 'marcas'] as const).map(p => (
                  <button key={p} onClick={() => setPosicion(p)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-black tracking-wide transition-all ${posicion === p ? 'bg-azul text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                    {p === 'superior' ? 'Superior' : p === 'inferior' ? 'Inferior' : 'Logos de proveedores'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Titulo (opcional)</label>
              <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
                placeholder="Ej: Promocion de verano"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-azul"/>
            </div>
            <div>
              <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">URL al hacer clic (opcional)</label>
              <input type="url" value={urlDestino} onChange={e => setUrlDestino(e.target.value)}
                placeholder="https://ticobaterias.com/catalogo"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-azul"/>
            </div>
            <div>
              <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Imagen del banner</label>
              <div onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-azul hover:bg-blue-50/30 transition-all">
                <div className="text-3xl mb-2">🖼️</div>
                <div className="text-sm font-bold text-gray-500">Clic para seleccionar imagen</div>
                <div className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP · Max 10MB · Recomendado: 1200x400px</div>
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden"/>
            </div>
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Vista previa</label>
            <div className="flex-1 bg-gray-50 rounded-xl border border-gray-100 overflow-hidden flex items-center justify-center min-h-[200px]">
              {preview ? <img src={preview} alt="Preview" className="w-full h-full object-cover"/> :
                <div className="text-center text-gray-300"><div className="text-4xl mb-2">📷</div><div className="text-sm">Sin imagen seleccionada</div></div>}
            </div>
          </div>
        </div>
        {error && <div className="mt-4 bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl font-bold">{error}</div>}
        {exito && <div className="mt-4 bg-green-50 text-green-600 text-sm px-4 py-3 rounded-xl font-bold">{exito}</div>}
        <button onClick={subir} disabled={subiendo || !archivo}
          className="mt-5 bg-azul text-white px-8 py-3 rounded-xl font-black text-sm tracking-wide hover:bg-azul-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed">
          {subiendo ? 'Subiendo...' : 'Subir Banner'}
        </button>
      </div>

      {cargando ? <div className="text-center py-12 text-gray-400">Cargando banners...</div> : (
        <>
          <BannerGroup titulo="Banners Superiores" banners={superiores} posicionGrupo="superior"
            onEliminar={eliminar} onToggle={toggleActivo}
            onDragStart={onDragStart} onDragEnter={onDragEnter} onDragEnd={onDragEnd}/>
          <BannerGroup titulo="Banners Inferiores" banners={inferiores} posicionGrupo="inferior"
            onEliminar={eliminar} onToggle={toggleActivo}
            onDragStart={onDragStart} onDragEnter={onDragEnter} onDragEnd={onDragEnd}/>
          <BannerGroup titulo="Logos de proveedores (franja de marcas)" banners={logosMarcas} posicionGrupo="marcas"
            onEliminar={eliminar} onToggle={toggleActivo}
            onDragStart={onDragStart} onDragEnter={onDragEnter} onDragEnd={onDragEnd}/>
        </>
      )}
    </div>
  );
}

function BannerGroup({ titulo, banners, posicionGrupo, onEliminar, onToggle, onDragStart, onDragEnter, onDragEnd }: {
  titulo: string; banners: Banner[]; posicionGrupo: string;
  onEliminar: (id: number) => void; onToggle: (id: number) => void;
  onDragStart: (i: number) => void; onDragEnter: (i: number) => void;
  onDragEnd: (pos: string) => void;
}) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-black text-azul text-lg">{titulo}</h2>
        {banners.length > 1 && <span className="text-xs bg-blue-50 text-azul px-3 py-1 rounded-full font-bold">Arrastra para reordenar</span>}
      </div>
      {banners.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-8 text-center text-gray-400 border border-dashed border-gray-200">No hay banners en esta posicion</div>
      ) : (
        <div className="grid gap-4">
          {banners.map((b, index) => (
            <div key={b.id}
              draggable
              onDragStart={() => onDragStart(index)}
              onDragEnter={() => onDragEnter(index)}
              onDragEnd={() => onDragEnd(posicionGrupo)}
              onDragOver={e => e.preventDefault()}
              className={`bg-white rounded-2xl border-2 overflow-hidden transition-all cursor-grab select-none ${b.activo ? 'border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md' : 'border-gray-200 opacity-60'}`} style={{transform: 'scale(1)', outline: 'none'}}>
              <div className="grid md:grid-cols-[280px_1fr] divide-y md:divide-y-0 md:divide-x divide-gray-100">
                <div className="relative h-36 bg-gray-50 overflow-hidden">
                  <img src={b.url.startsWith("http") ? b.url : "https://ticobaterias.com" + b.url} alt={b.titulo || 'Banner'} className="w-full h-full object-cover"/>
                  <div className="absolute top-2 left-2 bg-black/40 text-white text-xs px-2 py-1 rounded font-bold">#{index + 1}</div>
                  <div className="absolute top-2 right-2 text-white text-lg opacity-60">⠿</div>
                </div>
                <div className="p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded font-black ${b.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {b.activo ? 'Activo' : 'Inactivo'}
                      </span>
                      <span className="text-xs text-gray-400">{b.posicion}</span>
                    </div>
                    <div className="font-black text-azul text-base">{b.titulo || 'Sin titulo'}</div>
                    {b.url_destino && <div className="text-xs text-gray-400 mt-1 truncate">{b.url_destino}</div>}
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button onClick={() => onToggle(b.id)}
                      className={`px-4 py-2 rounded-lg text-xs font-black transition-all ${b.activo ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                      {b.activo ? 'Desactivar' : 'Activar'}
                    </button>
                    <button onClick={() => onEliminar(b.id)}
                      className="px-4 py-2 rounded-lg text-xs font-black bg-red-50 text-red-600 hover:bg-red-100 transition-all">
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
